<template>
  <w-sliding-popup ref="slidingPopup" title="로그 상세 정보">
    <div slot="content">
      <div class="title-bar title-h4">로그 요약</div>
      <el-row class="mr-b25">
        <div class="list form-list">
          <el-form ref="form" label-position="left">
            <el-form-item label="상태" label-width="120px">{{getIsChecked(selectedLog.isChecked)}}</el-form-item>
            <el-form-item label="로그종류" label-width="120px">{{getValueAfterNullCheck(selectedLog.logType)}}</el-form-item>
            <el-form-item label="관리대상명" label-width="120px">{{getValueAfterNullCheck(selectedLog.objName)}}</el-form-item>
            <el-form-item label="저장소" label-width="120px">{{getValueAfterNullCheck(selectedLog.sourceName)}}</el-form-item>
            <el-form-item label="필터" label-width="120px">{{getValueAfterNullCheck(selectedLog.filterName)}}</el-form-item>
            <el-form-item label="Severity" label-width="120px" v-show="selectedLog.logType === 'SYSLOG'">{{getValueAfterNullCheck(selectedLog.severity)}}</el-form-item>
            <el-form-item label="이벤트 등급" label-width="120px" v-show="selectedLog.logType !== 'SYSLOG'">{{getValueAfterNullCheck(selectedLog.severity)}}</el-form-item>
            <el-form-item label="수집시각" label-width="120px">{{convertedDatetime}}</el-form-item>
            <el-form-item label="메시지" label-width="120px">{{getValueAfterNullCheck(selectedLog.message)}}</el-form-item>
            <el-form-item label="Generic" label-width="120px" v-if="selectedLog.logType === 'TRAP'">{{getValueAfterNullCheck(snmptrapName)}}</el-form-item>
            <el-form-item label="Specific" label-width="120px" v-if="selectedLog.logType === 'TRAP'">{{getValueAfterNullCheck(messageDetail.specific)}}</el-form-item>
            <el-form-item label="상세 메시지" label-width="120px" v-if="selectedLog.logType === 'TRAP'">
              <div v-html="getMessageDetail(messageDetail.sub)"></div>
            </el-form-item>
          </el-form>
        </div>
      </el-row>
      <div class="title-bar title-h4">장비 정보</div>
      <w-server-obj-info class="mr-b25" v-if="selectedLog.logType === 'APP'" :obj-info="objInfo"></w-server-obj-info>
      <w-netdevice-obj-info class="mr-b25" v-else :obj-info="objInfo"></w-netdevice-obj-info>
      <div class="title-bar title-h4">장비 로그 이력</div>
      <el-row v-loading="loading">
        <highcharts class="chart" :options="options" />
      </el-row>
    </div>
  </w-sliding-popup>
</template>

<script>
import { mapGetters } from 'vuex';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import PerfNetAPI from '@/api/performance/netdevice/netdevice-info';
import PerfServerAPI from '@/api/performance/server/info-table';
import LogAPI from '@/api/log/common';

import { lineOptions } from '@/constants/chart-options';
import { EMPTY_TEXT } from '@/utils/string';
import { convertValueByUnitData } from '@/utils/number';
import msgUtils from '@/utils/message';
import { getDateToString, getRequestDatetimeParams, getIntervalDatetimeParams } from '@/utils/date';
import { stringify } from 'querystring';

import WServerObjInfo from '../ServerObjInfo';
import WNetdeviceObjInfo from '../NetdeviceObjInfo';

export default {
  name: 'WLogDetailPopup',
  components: {
    WServerObjInfo,
    WNetdeviceObjInfo,
  },
  props: {
    selectedLog: Object,
  },
  data() {
    return {
      loading: false,
      options: cloneDeep(lineOptions),
      objInfo: {},
      messageDetail: {},
      snmptrapName: '',
    };
  },
  computed: {
    ...mapGetters(['selectedNode', 'analysisDateParams']),
    convertedDatetime() {
      const datetime = this.selectedLog['@timestamp'];
      if (!isNil(datetime)) {
        return getDateToString(datetime);
      }
      return EMPTY_TEXT;
    },
  },
  watch: {
    selectedLog: {
      immediate: true,
      deep: true,
      handler() {
        if (this.selectedLog.logType === 'TRAP') {
          if (!isEmpty(this.selectedLog.messageDetail)) {
            this.messageDetail = JSON.parse(this.selectedLog.messageDetail);
          }
        }
        if (!isEmpty(this.selectedLog)) {
          this.loadData();
          this.options = cloneDeep(lineOptions);
          this.loadChartData();
        }
      },
    },
  },
  methods: {
    /**
     * objId로 서버/네트워크 장비의 상세정보 데이터를 가져온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadData() {
      // TODO 서버인지 네트워크 장비인지 구분할 수 있는 기준값 필요(현재는 로그타입으로 구분)
      const params = {
        objId: this.selectedLog.objId,
      };
      if (this.selectedLog.logType === 'APP') {
        // APPLOG일 때 > 서버 장비
        PerfServerAPI.getServerConfigurationInfo(params).then((response) => {
          if (response.data.success && !isEmpty(response.data.data)) {
            const configInfo = response.data.data;
            configInfo.majorLabel = !isEmpty(configInfo.majorName) ? `[${configInfo.majorDepartment}]${configInfo.majorName}` : '-'; // TODO: String util의 getValueAfterCheckEmpty function 활용 (utils/string > getValueAfterCheckEmpty)
            configInfo.minorLabel = !isEmpty(configInfo.minorName) ? `[${configInfo.minorDepartment}]${configInfo.minorName}` : '-';
            configInfo.checkDatetimeLabel = !isEmpty(configInfo.checkDatetime) ? moment(configInfo.checkDatetime).format('YYYY년 MM월 DD일 HH:mm:ss') : '-';
            this.objInfo = configInfo;
          }
        }).catch((e) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '장비 정보', false));
          throw new Error(e);
        });
      } else {
        // SYSLOG일 때 > 네트워크 장비
        PerfNetAPI.getNetdeviceConfigurationInfo(params).then((response) => {
          if (response.data.success && !isEmpty(response.data.data)) {
            const configInfo = response.data.data;
            configInfo.majorLabel = !isEmpty(configInfo.majorName) ? `[${configInfo.majorDepartment}]${configInfo.majorName}` : '-'; // TODO: String util의 getValueAfterCheckEmpty function 활용 (utils/string > getValueAfterCheckEmpty)
            configInfo.minorLabel = !isEmpty(configInfo.minorName) ? `[${configInfo.minorDepartment}]${configInfo.minorName}` : '-';
            configInfo.checkDatetimeLabel = !isEmpty(configInfo.checkDatetime) ? moment(configInfo.checkDatetime).format('YYYY년 MM월 DD일 HH:mm:ss') : '-';
            configInfo.netdeviceUptimeLabel = !isNil(configInfo.netdeviceUptimeCount) ? `${configInfo.netdeviceUptimeCount}일` : EMPTY_TEXT;
            // configInfo.netdeviceUptimeLabel = calcUptime(new Date(configInfo.netdeviceUptime), new Date());
            this.objInfo = configInfo;
          }
        }).catch((e) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '장비 정보', false));
          throw new Error(e);
        });
        if (this.selectedLog.logType === 'TRAP') {
          //  snmpTrap일 때 > snmpName
          const queryString = stringify({ generic: this.messageDetail.generic });
          LogAPI.getSnmpTrapName(queryString).then((response) => {
            if (response.data.success) {
              this.snmptrapName = response.data.data;
            }
          }).catch((e) => {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '장비 정보', false));
            throw new Error(e);
          });
        }
      }
    },
    /**
     * 로그 상세 보기의 차트 데이터를 가져온다. (해당 로그의 장비 기준)
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadChartData() {
      const endDateTime = getRequestDatetimeParams(this.selectedLog['@timestamp']);
      const startDateTime = getIntervalDatetimeParams(this.selectedLog['@timestamp'], 'hour', 24);
      const params = {
        objId: this.selectedLog.objId,
        objName: this.selectedLog.objName,
        logType: this.selectedLog.logType,
        startDateTime,
        endDateTime,
      };
      this.loading = true;
      LogAPI.getLogDetailChartData(params).then((response) => {
        if (response.data.success) {
          const series = [];
          series.push(response.data.data);
          this.options.series = series;
          this.options.tooltip = {
            xDateFormat: '%Y-%m-%d %H:%M:%S',
            split: false,
            shared: true,
            valueDecimals: 0,
            valueSuffix: '개',
            formatter() {
              let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm:ss');
              this.points.forEach((point) => {
                const yValue = convertValueByUnitData(point.y, 'count', '개', 1, null, null, true);
                tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${yValue}</b><br/>`;
              });
              return tooltip;
            },
          };
          this.options.chart.height = 200;
        } else {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그 이력 차트', false));
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그 이력 차트', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    getValueAfterNullCheck(value) {
      return !isNil(value) ? value : EMPTY_TEXT;
    },
    getIsChecked(value) {
      if (value === '0') {
        return '미확인';
      } else if (value === '1') {
        return '확인';
      }

      return EMPTY_TEXT;
    },
    getMessageDetail(message) {
      let messageDetail = '';
      if (!isEmpty(message)) {
        message.forEach((item, index) => {
          messageDetail += `&nbsp;&nbsp;&nbsp;snmpOid : ${item.snmpOid}<br/>`;
          messageDetail += `&nbsp;&nbsp;&nbsp;snmpType : ${item.snmpType}<br/>`;
          messageDetail += `&nbsp;&nbsp;&nbsp;vIndex : ${item.vIndex}<br/>`;
          messageDetail += `&nbsp;&nbsp;&nbsp;value : ${item.value}<br/>`;
          if (message.length > 2 && message.length !== index + 1) {
            messageDetail += '<hr/>';
          }
        });
      }
      return messageDetail;
    },
    close() {
      this.objInfo = {};
      this.$refs.slidingPopup.close();
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/log/components/popup/LogDetailPopup.vue